import { useState, useEffect, useRef } from 'react'
import { ArrowRight, ArrowUpRight, Check, Menu, X } from 'lucide-react'
import { BuyMeACoffeeButton } from './BuyMeACoffeeButton'

const PHRASES = [
  "Define success metrics",
  "Review Q2 OKRs",
  "Plan team offsite",
  "Update roadmap"
]

function Typewriter() {
  const [text, setText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [loopNum, setLoopNum] = useState(0)
  const [typingSpeed, setTypingSpeed] = useState(100)

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>

    const handleTyping = () => {
      const i = loopNum % PHRASES.length
      const fullText = PHRASES[i]

      if (!isDeleting && text === fullText) {
        timer = setTimeout(() => setIsDeleting(true), 2000)
        return
      }

      if (isDeleting && text === '') {
        timer = setTimeout(() => {
          setIsDeleting(false)
          setLoopNum(loopNum + 1)
        }, 500)
        return
      }

      setText(
        isDeleting
          ? fullText.substring(0, text.length - 1)
          : fullText.substring(0, text.length + 1)
      )

      setTypingSpeed(isDeleting ? 30 : 100)
    }

    timer = setTimeout(handleTyping, typingSpeed)
    return () => clearTimeout(timer)
  }, [text, isDeleting, loopNum, typingSpeed])

  return <span>{text}</span>
}

export function HomePage() {
  const [isDark, setIsDark] = useState(() => {
    if (typeof window === 'undefined') return false
    const stored = localStorage.getItem('theme')
    if (stored === 'dark') return true
    if (stored === 'light') return false
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  })
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [showIntro, setShowIntro] = useState(() => {
    if (typeof window === 'undefined') return false
    if (sessionStorage.getItem('jot-intro-seen')) return false
    return true
  })
  const [introText, setIntroText] = useState('')
  const [introFading, setIntroFading] = useState(false)
  const heroRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    document.title = 'JOT Editor — write first, everything else later'
  }, [])

  useEffect(() => {
    if (!showIntro) return
    const fullText = 'gojot.app'
    let i = 0
    
    const startDelay = setTimeout(() => {
      const interval = setInterval(() => {
        setIntroText(fullText.slice(0, i + 1))
        i++
        if (i === fullText.length) {
          clearInterval(interval)
          setTimeout(() => {
            setIntroFading(true)
            setTimeout(() => {
              setShowIntro(false)
              sessionStorage.setItem('jot-intro-seen', '1')
            }, 600)
          }, 800)
        }
      }, 120)
      return () => clearInterval(interval)
    }, 400)
    
    return () => clearTimeout(startDelay)
  }, [showIntro])

  useEffect(() => {
    if (showIntro) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [showIntro])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    document.documentElement.classList.toggle('lp-dark', isDark)
    document.documentElement.classList.toggle('dark', isDark)
    localStorage.setItem('theme', isDark ? 'dark' : 'light')
    return () => {
      document.documentElement.classList.remove('lp-dark')
      // We don't remove 'dark' here because the user might navigate to the app
    }
  }, [isDark])

  return (
    <div className={`lp${isDark ? ' lp--dark' : ''}`}>
      {showIntro && (
        <div className={`lp-intro${introFading ? ' lp-intro--fade' : ''}`}>
          <div className="lp-intro-text">
            {introText}<span className="lp-intro-cursor" />
          </div>
        </div>
      )}

      {/* ─── NAV ───────────────────────────────────────────────── */}
      <nav className={`lp-nav${scrolled ? ' lp-nav--scrolled' : ''}`}>
        <div className="lp-nav-inner">
          <a href="/home" className="lp-wordmark">JOT</a>

          <div className="lp-nav-center">
            <a href="/home" className="lp-nav-link">Clipboard</a>
            <a href="/editor" className="lp-nav-link">Editor</a>
            <a href="#features" className="lp-nav-link">Features</a>
            <a href="#pricing"  className="lp-nav-link">Pricing</a>
            <a href="/blogs" className="lp-nav-link">Journey</a>
          </div>

          <div className="lp-nav-right">
            <button className="lp-theme-toggle" onClick={() => setIsDark(d => !d)} aria-label="Toggle theme">
              <span className="lp-theme-track">
                <span className={`lp-theme-thumb${isDark ? ' lp-theme-thumb--on' : ''}`} />
              </span>
              <span className="lp-theme-label">{isDark ? 'Light' : 'Dark'}</span>
            </button>
            <a href="/" className="lp-open-btn">Open app <ArrowUpRight className="w-3.5 h-3.5" /></a>
            <button className="lp-hamburger" onClick={() => setMobileMenuOpen(o => !o)}>
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lp-mobile-nav">
            <a href="/home" onClick={() => setMobileMenuOpen(false)}>Clipboard</a>
            <a href="/editor" onClick={() => setMobileMenuOpen(false)}>Editor</a>
            <a href="#features" onClick={() => setMobileMenuOpen(false)}>Features</a>
            <a href="#pricing"  onClick={() => setMobileMenuOpen(false)}>Pricing</a>
            <a href="/blogs" onClick={() => setMobileMenuOpen(false)}>Journey</a>
            <a href="/" className="lp-mobile-cta">Open app →</a>
          </div>
        )}
      </nav>

      {/* ─── HERO ──────────────────────────────────────────────── */}
      <section className="lp-hero" ref={heroRef}>
        <div className="lp-hero-inner">

          <div className="lp-hero-left">
            <p className="lp-hero-eyebrow">A note-taking app for people who think fast</p>

            <h1 className="lp-hero-headline">
              Write first.<br />Everything <em>else</em> later.
            </h1>

            <p className="lp-hero-body">
              JOT opens in under a second, and stores your notes locally
              by default. No account. No friction. Just a blank page.
            </p>

            <div className="lp-hero-actions">
              <a href="/" className="lp-cta-primary">
                Start writing — it's free
                <ArrowRight className="w-4 h-4" />
              </a>
              <span className="lp-hero-proof">No sign-up &nbsp;·&nbsp; Works offline &nbsp;·&nbsp; Instant</span>
            </div>
          </div>

          <div className="lp-hero-right" aria-hidden>
            <div className="lp-type-block">
              <div className="lp-type-toolbar">
                <span className="lp-type-brand">JOT</span>
                <span className="lp-type-title-bar">Q3 Planning · Jun 12</span>
                <span className="lp-type-dot" />
              </div>
              <div className="lp-type-body">
                <div className="lp-type-h1">Q3 Planning Session</div>
                <div className="lp-type-spacer" />
                <div className="lp-type-p">Goals this quarter:</div>
                <div className="lp-type-bullet">Launch redesigned onboarding</div>
                <div className="lp-type-bullet">Ship mobile parity</div>
                <div className="lp-type-bullet">Cut sync latency ≤ 200 ms</div>
                <div className="lp-type-spacer" />
                <div className="lp-type-muted"><Typewriter /><span className="lp-cursor" /></div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ─── FEATURES BENTO ────────────────────────────────────── */}
      <section id="features" className="lp-section">
        <div className="lp-section-inner">

          <div className="lp-section-header">
            <span className="lp-label">Features</span>
            <h2 className="lp-section-title">Built for thinking,<br/>not for managing a tool.</h2>
          </div>

          <div className="lp-bento">

            {/* A — local first, wide */}
            <div className="lp-card lp-card--wide">
              <div className="lp-card-num">01</div>
              <h3 className="lp-card-title">Local-first, always.</h3>
              <p className="lp-card-desc">
                Your notes live in your browser — completely offline, completely private.
                No server knows what you write. Cloud sync is opt-in, never assumed.
              </p>
              <span className="lp-card-chip">Zero data collection</span>
            </div>

            {/* B — slash commands, accent tall */}
            <div className="lp-card lp-card--tall lp-card--accent">
              <div className="lp-card-num lp-card-num--light">02</div>
              <h3 className="lp-card-title lp-card-title--light">Slash commands.</h3>
              <p className="lp-card-desc lp-card-desc--light">
                Type <span className="lp-inline-cmd">/</span> to turn any line into a heading,
                list, quote, code block, or image.
              </p>
              <div className="lp-cmd-chips">
                {['/heading', '/bullet', '/code', '/quote', '/image'].map(cmd => (
                  <span key={cmd} className="lp-cmd-chip">{cmd}</span>
                ))}
              </div>
            </div>

            {/* C — stat */}
            <div className="lp-card lp-card--small lp-card--stat">
              <div className="lp-big-num">~0.4s</div>
              <div className="lp-stat-label">Cold start to writing</div>
            </div>

            {/* D — command palette */}
            <div className="lp-card lp-card--small">
              <div className="lp-kbd-pair">
                <kbd className="lp-sc-key">⌘</kbd><kbd className="lp-sc-key">K</kbd>
              </div>
              <h3 className="lp-card-title" style={{ marginTop: '0.875rem', fontSize: '1rem' }}>Command palette</h3>
              <p className="lp-card-desc" style={{ fontSize: '0.82rem', marginTop: '0.35rem' }}>
                Search, switch, pin, delete — anything from the keyboard.
              </p>
            </div>

            {/* E — sync, wide */}
            <div className="lp-card lp-card--wide lp-card--sync">
              <div className="lp-sync-row">
                <span className="lp-sync-dot" />
                <span className="lp-sync-text">Synced · 2 devices</span>
              </div>
              <div className="lp-card-num">03</div>
              <h3 className="lp-card-title">Sync that's quiet — and free.</h3>
              <p className="lp-card-desc">
                One green dot. That's the entire sync UI. Sign in and your notes
                follow you everywhere — Mac, iPhone, anywhere.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ─── SHORTCUTS STRIP ───────────────────────────────────── */}
      <section className="lp-strip">
        <div className="lp-strip-inner">
          <p className="lp-strip-label">Keyboard shortcuts</p>
          <div className="lp-strip-row">
            {[
              { keys: ['⌘','K'], label: 'Command palette' },
              { keys: ['/'],     label: 'Insert block' },
              { keys: ['⌘','B'], label: 'Bold' },
              { keys: ['⌘','I'], label: 'Italic' },
              { keys: ['⌘','Z'], label: 'Undo' },
              { keys: ['Esc'],   label: 'Close / cancel' },
            ].map(sc => (
              <div key={sc.label} className="lp-sc-item">
                <div className="lp-sc-keys">
                  {sc.keys.map((k, i) => <kbd key={i} className="lp-sc-key">{k}</kbd>)}
                </div>
                <span className="lp-sc-label">{sc.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PRICING ───────────────────────────────────────────── */}
      <section id="pricing" className="lp-section lp-section--alt">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <span className="lp-label">Pricing</span>
            <h2 className="lp-section-title">Free.<br/>Including sync.</h2>
            <p className="lp-section-sub">Sign in and your notes follow you. If JOT helps, you can buy me a coffee.</p>
          </div>

          <div className="lp-price-free">
            <ul className="lp-price-free-list">
              {[
                'Unlimited notes',
                'Rich text editor',
                'Cloud sync across devices',
                'Works offline',
                'Light & dark mode',
              ].map(label => (
                <li key={label}>
                  <Check className="w-4 h-4 lp-check lp-check--gold" />
                  {label}
                </li>
              ))}
            </ul>
            <div className="lp-price-free-cta">
              <a href="/" className="lp-price-btn lp-price-btn--gold">Start writing</a>
              <BuyMeACoffeeButton source="homepage_pricing" />
            </div>
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─────────────────────────────────────────── */}
      <section className="lp-final">
        <div className="lp-final-inner">
          <div className="lp-final-wordmark">JOT</div>
          <p className="lp-final-sub">Open the app. Start writing. That's it.</p>
          <a href="/" className="lp-cta-primary lp-cta-lg">
            Get started — free <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* ─── FOOTER ────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <span className="lp-footer-wordmark">JOT</span>
          <span className="lp-footer-copy">© {new Date().getFullYear()} JOT. All rights reserved.</span>
          <div className="lp-footer-links">
            <a href="/home" className="lp-footer-link">Clipboard</a>
            <a href="/editor" className="lp-footer-link">Editor</a>
            <a href="/" className="lp-footer-link">App</a>
            <a href="/blogs" className="lp-footer-link">Journey</a>
          </div>
        </div>
      </footer>

    </div>
  )
}
